/**
 * 네비게이션 유틸리티
 * 히스토리 관리 및 뒤로가기 기능 제공
 */
import type { NavigateFunction } from 'react-router-dom'

/**
 * SPA 히스토리에 돌아갈 곳이 남아 있는지.
 *
 * react-router가 history.state.idx에 엔트리 인덱스를 기록한다(첫 진입 = 0).
 * 네이티브가 알림 이동용으로 history.pushState({})를 부르면 idx가 지워지므로,
 * 그때는 브라우저 히스토리 길이로 판단한다.
 */
export const canGoBackInApp = (): boolean => {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx
  return typeof idx === 'number' ? idx > 0 : window.history.length > 1
}

/**
 * 알림을 눌러 들어온 화면인지.
 * 네이티브가 부른 pushState를 가로챌 때 history.state에 표시해 둔다(useSwipeBack).
 */
export const isFromNotification = (): boolean => {
  const state = window.history.state as { fromNotification?: unknown } | null
  return state?.fromNotification === true
}

/**
 * 뒤로가기. 돌아갈 곳이 없거나 알림으로 진입한 화면이면 홈으로 보낸다.
 *
 * - 알림 딥링크로 콜드스타트하면 그 페이지가 히스토리의 첫 엔트리라
 *   navigate(-1)이 아무 동작도 하지 않아 화면이 멈춘 것처럼 보인다.
 * - 앱이 떠 있는 상태에서 알림을 눌러 들어온 경우엔 히스토리가 있지만,
 *   직전 화면이 이전 알림으로 열린 무관한 페이지일 수 있다. 알림은 브라우징의
 *   연장이 아니라 진입점이므로 홈으로 보낸다.
 */
export const goBackOrHome = (navigate: NavigateFunction): void => {
  if (!isFromNotification() && canGoBackInApp()) {
    navigate(-1)
    return
  }
  navigate('/main-home', { replace: true })
}

/**
 * 경로에서 쿼리 파라미터를 제거하고 경로만 반환
 * @param path - 전체 경로 (예: /search?q=맛집)
 * @returns 경로만 반환 (예: /search)
 */
const getPathWithoutQuery = (path: string): string => {
  return path.split('?')[0]
}

/**
 * 현재 경로를 히스토리에 추가
 * 페이지 진입 시 호출하여 경로 히스토리를 추적
 * 쿼리 파라미터는 무시하고 경로만 저장
 * @param currentPath - 현재 경로 (선택사항, 없으면 window.location.pathname 사용)
 */
export const addPathToHistory = (currentPath?: string): void => {
  const fullPath = currentPath || window.location.pathname
  const path = getPathWithoutQuery(fullPath)

  // sessionStorage에서 경로 히스토리 가져오기
  const historyKey = 'navigation_history'
  const historyStr = sessionStorage.getItem(historyKey)
  const history: string[] = historyStr ? JSON.parse(historyStr) : []

  // 현재 경로를 히스토리에 추가 (중복 제거)
  // 마지막 항목과 같으면 추가하지 않음 (쿼리 파라미터만 다른 경우)
  const lastPath = history.length > 0 ? history[history.length - 1] : null
  if (lastPath !== path) {
    const updatedHistory = [...history, path]

    // 최근 10개만 유지
    const maxHistory = 10
    const trimmedHistory =
      updatedHistory.length > maxHistory
        ? updatedHistory.slice(-maxHistory)
        : updatedHistory

    sessionStorage.setItem(historyKey, JSON.stringify(trimmedHistory))
  }
}

/**
 * 히스토리에 동일한 라우트가 있으면 건너뛰고 뒤로가기
 * 쿼리 파라미터는 무시하고 경로만 비교
 * 실제 브라우저 히스토리를 확인하여 동일한 경로를 건너뜀
 * @param navigate - React Router의 navigate 함수
 * @param currentPath - 현재 경로 (선택사항, 없으면 window.location.pathname 사용)
 */
export const navigateBackSkipSameRoute = (
  navigate: (delta: number) => void,
  currentPath?: string
): void => {
  const fullPath = currentPath || window.location.pathname
  const currentRoute = getPathWithoutQuery(fullPath)

  // sessionStorage에서 경로 히스토리 가져오기
  const historyKey = 'navigation_history'
  const historyStr = sessionStorage.getItem(historyKey)
  const history: string[] = historyStr ? JSON.parse(historyStr) : []

  // 뒤로가기 시 건너뛸 횟수 계산
  // 히스토리의 마지막에서 두 번째부터 역순으로 확인
  let skipCount = 0
  for (let i = history.length - 2; i >= 0; i--) {
    if (history[i] === currentRoute) {
      skipCount++
    } else {
      break
    }
  }

  // 동일한 경로가 있으면 그만큼 더 뒤로 가기
  // 하지만 실제 브라우저 히스토리와 동기화하기 위해
  // 한 번씩 뒤로 가면서 경로를 확인하는 방식 사용
  if (skipCount > 0) {
    // 동일한 경로가 여러 개 있으면 모두 건너뛰기
    navigate(-(1 + skipCount))
  } else {
    // 동일한 경로가 없으면 일반 뒤로가기
    navigate(-1)
  }
}

/**
 * 경로 히스토리 초기화
 */
export const clearNavigationHistory = (): void => {
  sessionStorage.removeItem('navigation_history')
}
